import {
    ArrowLeft,
    Building2,
    Calendar,
    Eye,
    MapPin,
    Save,
    Settings,
    X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';

const RMCBuildingEdit = () => {
  const navigate = useNavigate();
  const { buildingId } = useParams();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('basic');
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    building_type: '',
    total_floors: '',
    total_units: '',
    year_built: '',
    building_code: '',
    land_area: '',
    building_area: '',
    floor_area_ratio: '',
    building_coverage_ratio: '',
    parking_spaces: '',
    elevator_count: '',
    management_company: '',
    management_phone: '',
    monthly_maintenance_fee: '',
    security_company: '',
    security_phone: '',
    heating_type: '',
    heating_fee: '',
    status: 'active',
    notes: '',
    // 새로 추가된 필드들
    building_purpose: '',
    total_floor_area: '',
    building_structure: '',
    has_elevator: false
  });

  // 시설 정보 상태
  const [facilityData, setFacilityData] = useState({
    electric_capacity: '',
    water_meter_count: '',
    gas_type: '',
    heating_type: '',
    has_cctv: false
  });

  // 상태 정보 상태
  const [conditionData, setConditionData] = useState({
    exterior_crack_status: '양호',
    paint_status: '양호',
    roof_waterproof_status: '양호',
    water_supply_status: '양호',
    boiler_status: '양호',
    fire_safety_status: '양호',
    electrical_wiring_status: '양호',
    corridor_status: '양호',
    parking_status: '양호',
    lighting_status: '양호',
    cleaning_status: '양호'
  });

  // 점검 이력 상태
  const [inspectionData, setInspectionData] = useState([]);

  // 건물 상세 정보 가져오기
  const { data: buildingData, isLoading, error } = useQuery(
    ['rmc-building', buildingId],
    () => api.get(`/rmc/buildings/${buildingId}`)
  );

  // 데이터 로딩 후 폼에 설정
  useEffect(() => {
    if (buildingData && buildingData.building && !isLoading) {
      console.log('🔍 건물 데이터 로딩 성공:', buildingData);
      const building = buildingData.building;
      console.log('🏢 건물 정보:', building);
      
      setFormData({
        name: building.name || '',
        address: building.address || '',
        building_type: building.building_type || '',
        total_floors: building.total_floors || '',
        total_units: building.total_units || '',
        year_built: building.year_built || '',
        building_code: building.building_code || '',
        land_area: building.land_area || '',
        building_area: building.building_area || '',
        floor_area_ratio: building.floor_area_ratio || '',
        building_coverage_ratio: building.building_coverage_ratio || '',
        parking_spaces: building.parking_spaces || '',
        elevator_count: building.elevator_count || '',
        management_company: building.management_company || '',
        management_phone: building.management_phone || '',
        monthly_maintenance_fee: building.monthly_maintenance_fee || '',
        security_company: building.security_company || '',
        security_phone: building.security_phone || '',
        heating_type: building.heating_type || '',
        heating_fee: building.heating_fee || '',
        status: building.status || 'active',
        notes: building.notes || '',
        // 새로 추가된 필드들
        building_purpose: building.building_purpose || '',
        total_floor_area: building.total_floor_area || '',
        building_structure: building.building_structure || '',
        has_elevator: building.has_elevator || false
      });
      console.log('📝 폼 데이터 설정 완료:', {
        name: building.name || '',
        address: building.address || '',
        building_type: building.building_type || '',
        total_floors: building.total_floors || '',
        building_purpose: building.building_purpose || '',
        total_floor_area: building.total_floor_area || '',
        building_structure: building.building_structure || '',
        has_elevator: building.has_elevator || false
      });

      // 시설 정보 설정
      setFacilityData({
        electric_capacity: building.electric_capacity || '',
        water_meter_count: building.water_meter_count || '',
        gas_type: building.gas_type || '',
        heating_type: building.heating_type || '',
        has_cctv: building.has_cctv || false
      });

      // 상태 정보 설정
      setConditionData({
        exterior_crack_status: building.exterior_crack_status || '양호',
        paint_status: building.paint_status || '양호',
        roof_waterproof_status: building.roof_waterproof_status || '양호',
        water_supply_status: building.water_supply_status || '양호',
        boiler_status: building.boiler_status || '양호',
        fire_safety_status: building.fire_safety_status || '양호',
        electrical_wiring_status: building.electrical_wiring_status || '양호',
        corridor_status: building.corridor_status || '양호',
        parking_status: building.parking_status || '양호',
        lighting_status: building.lighting_status || '양호',
        cleaning_status: building.cleaning_status || '양호'
      });
    }
  }, [buildingData, isLoading]);

  // 오류 처리
  useEffect(() => {
    if (error) {
      console.error('❌ 건물 데이터 로딩 실패:', error);
    }
  }, [error]);

  // 건물 수정 뮤테이션
  const updateBuildingMutation = useMutation(
    (data) => api.put(`/rmc/buildings/${buildingId}`, data),
    {
      onSuccess: () => {
        toast.success('건물 정보가 성공적으로 수정되었습니다.');
        queryClient.invalidateQueries('rmc-buildings');
        queryClient.invalidateQueries(['rmc-building', buildingId]);
        navigate(`/rmc/buildings/${buildingId}`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '건물 수정에 실패했습니다.');
      }
    }
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFacilityChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFacilityData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleConditionChange = (e) => {
    const { name, value } = e.target;
    setConditionData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // 빈 값 처리 함수
    const processValue = (value, type = 'string') => {
      if (value === '' || value === null || value === undefined) {
        return null;
      }
      if (type === 'number') {
        const num = parseFloat(value);
        return isNaN(num) ? null : num;
      }
      if (type === 'integer') {
        const num = parseInt(value);
        return isNaN(num) ? null : num;
      }
      return value;
    };

    // 데이터 변환
    const submitData = {
      ...formData,
      // 숫자 필드 변환 (빈 값은 null로 처리)
      total_floors: processValue(formData.total_floors, 'integer'),
      total_units: processValue(formData.total_units, 'integer'),
      year_built: processValue(formData.year_built, 'integer'),
      land_area: processValue(formData.land_area, 'number'),
      building_area: processValue(formData.building_area, 'number'),
      floor_area_ratio: processValue(formData.floor_area_ratio, 'number'),
      building_coverage_ratio: processValue(formData.building_coverage_ratio, 'number'),
      parking_spaces: processValue(formData.parking_spaces, 'integer'),
      elevator_count: processValue(formData.elevator_count, 'integer'),
      monthly_maintenance_fee: processValue(formData.monthly_maintenance_fee, 'integer'),
      heating_fee: processValue(formData.heating_fee, 'integer'),
      total_floor_area: processValue(formData.total_floor_area, 'number'),
      // 문자열 필드도 빈 값 처리
      name: processValue(formData.name),
      address: processValue(formData.address),
      building_code: processValue(formData.building_code),
      management_company: processValue(formData.management_company),
      management_phone: processValue(formData.management_phone),
      security_company: processValue(formData.security_company),
      security_phone: processValue(formData.security_phone),
      heating_type: processValue(formData.heating_type),
      building_purpose: processValue(formData.building_purpose),
      building_structure: processValue(formData.building_structure),
      notes: processValue(formData.notes),
      // 시설 정보 추가
      ...facilityData,
      electric_capacity: processValue(facilityData.electric_capacity, 'integer'),
      water_meter_count: processValue(facilityData.water_meter_count, 'integer'),
      gas_type: processValue(facilityData.gas_type),
      // 상태 정보 추가
      ...conditionData
    };

    updateBuildingMutation.mutate(submitData);
  };

  console.log('🔄 컴포넌트 렌더링 상태:', { isLoading, buildingData, formData });

  if (isLoading) {
    console.log('⏳ 로딩 중...');
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const building = buildingData?.building;
  console.log('🏢 현재 building 객체:', building);

  if (!building) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">건물 정보를 찾을 수 없습니다.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex justify-between items-start">
        <div>
          <button
            onClick={() => navigate(`/rmc/buildings/${buildingId}`)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            건물 상세보기로 돌아가기
          </button>
          <h1 className="text-2xl font-bold text-gray-900">건물 정보 수정</h1>
          <p className="text-gray-600 flex items-center">
            <MapPin className="w-4 h-4 mr-1" />
            {building.name}
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate(`/rmc/buildings/${buildingId}`)}
            className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <X className="w-4 h-4 mr-2" />
            취소
          </button>
        </div>
      </div>

      {/* 탭 메뉴 */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'basic', name: '기본 정보', icon: Building2 },
            { id: 'facility', name: '시설 정보', icon: Settings },
            { id: 'condition', name: '상태 정보', icon: Eye },
            { id: 'inspection', name: '점검 이력', icon: Calendar },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* 탭 콘텐츠 */}
      {activeTab === 'basic' && (
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* 기본 정보 */}
          <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">기본 정보</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                건물명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                주소 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">건물 유형</label>
              <select
                name="building_type"
                value={formData.building_type}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">선택하세요</option>
                <option value="원룸">원룸</option>
                <option value="투룸">투룸</option>
                <option value="쓰리룸">쓰리룸</option>
                <option value="오피스텔">오피스텔</option>
                <option value="아파트">아파트</option>
                <option value="빌라">빌라</option>
                <option value="상가">상가</option>
                <option value="사무실">사무실</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">용도</label>
              <select
                name="building_purpose"
                value={formData.building_purpose}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">선택하세요</option>
                <option value="주거">주거</option>
                <option value="상업">상업</option>
                <option value="사무">사무</option>
                <option value="공업">공업</option>
                <option value="기타">기타</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">총 층수</label>
              <input
                type="number"
                name="total_floors"
                value={formData.total_floors}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                min="1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">총 세대수</label>
              <input
                type="number"
                name="total_units"
                value={formData.total_units}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                min="1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">연면적 (㎡)</label>
              <input
                type="number"
                name="total_floor_area"
                value={formData.total_floor_area}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                step="0.01"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">구조</label>
              <select
                name="building_structure"
                value={formData.building_structure}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">선택하세요</option>
                <option value="철근콘크리트">철근콘크리트</option>
                <option value="철골">철골</option>
                <option value="철골철근콘크리트">철골철근콘크리트</option>
                <option value="블록">블록</option>
                <option value="목조">목조</option>
                <option value="기타">기타</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">엘리베이터 유무</label>
              <select
                name="has_elevator"
                value={formData.has_elevator}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value={false}>없음</option>
                <option value={true}>있음</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">건축년도</label>
              <input
                type="number"
                name="year_built"
                value={formData.year_built}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                min="1900"
                max={new Date().getFullYear()}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">건물 코드</label>
              <input
                type="text"
                name="building_code"
                value={formData.building_code}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">상태</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="active">활성</option>
                <option value="inactive">비활성</option>
                <option value="maintenance">유지보수</option>
              </select>
            </div>
          </div>
        </div>

        {/* 건물 상세 정보 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">건물 상세 정보</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">대지면적 (㎡)</label>
              <input
                type="number"
                name="land_area"
                value={formData.land_area}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                step="0.01"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">건물면적 (㎡)</label>
              <input
                type="number"
                name="building_area"
                value={formData.building_area}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                step="0.01"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">용적률 (%)</label>
              <input
                type="number"
                name="floor_area_ratio"
                value={formData.floor_area_ratio}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                step="0.01"
                min="0"
                max="100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">건폐율 (%)</label>
              <input
                type="number"
                name="building_coverage_ratio"
                value={formData.building_coverage_ratio}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                step="0.01"
                min="0"
                max="100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">주차대수</label>
              <input
                type="number"
                name="parking_spaces"
                value={formData.parking_spaces}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">엘리베이터 수</label>
              <input
                type="number"
                name="elevator_count"
                value={formData.elevator_count}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                min="0"
              />
            </div>
          </div>
        </div>

        {/* 관리 정보 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">관리 정보</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">관리회사</label>
              <input
                type="text"
                name="management_company"
                value={formData.management_company}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">관리회사 연락처</label>
              <input
                type="text"
                name="management_phone"
                value={formData.management_phone}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">월 관리비 (원)</label>
              <input
                type="number"
                name="monthly_maintenance_fee"
                value={formData.monthly_maintenance_fee}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">경비회사</label>
              <input
                type="text"
                name="security_company"
                value={formData.security_company}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">경비회사 연락처</label>
              <input
                type="text"
                name="security_phone"
                value={formData.security_phone}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">난방 방식</label>
              <select
                name="heating_type"
                value={formData.heating_type}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">선택하세요</option>
                <option value="개별난방">개별난방</option>
                <option value="중앙난방">중앙난방</option>
                <option value="지역난방">지역난방</option>
                <option value="기타">기타</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">난방비 (원)</label>
              <input
                type="number"
                name="heating_fee"
                value={formData.heating_fee}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                min="0"
              />
            </div>
          </div>
        </div>

        {/* 비고 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">비고</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">메모</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={4}
              className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="건물에 대한 추가 정보나 메모를 입력하세요..."
            />
          </div>
        </div>

          {/* 저장 버튼 */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate(`/rmc/buildings/${buildingId}`)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={updateBuildingMutation.isLoading}
              className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4 mr-2" />
              {updateBuildingMutation.isLoading ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      )}

      {/* 시설 정보 탭 */}
      {activeTab === 'facility' && (
        <div className="space-y-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">시설 정보</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">전기용량 (kW)</label>
                <input
                  type="number"
                  name="electric_capacity"
                  value={facilityData.electric_capacity}
                  onChange={handleFacilityChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">수도계량기 수</label>
                <input
                  type="number"
                  name="water_meter_count"
                  value={facilityData.water_meter_count}
                  onChange={handleFacilityChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">가스방식</label>
                <select
                  name="gas_type"
                  value={facilityData.gas_type}
                  onChange={handleFacilityChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">선택하세요</option>
                  <option value="도시가스">도시가스</option>
                  <option value="LPG">LPG</option>
                  <option value="프로판가스">프로판가스</option>
                  <option value="기타">기타</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">난방형태</label>
                <select
                  name="heating_type"
                  value={facilityData.heating_type}
                  onChange={handleFacilityChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">선택하세요</option>
                  <option value="개별난방">개별난방</option>
                  <option value="중앙난방">중앙난방</option>
                  <option value="지역난방">지역난방</option>
                  <option value="기타">기타</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CCTV 유무</label>
                <select
                  name="has_cctv"
                  value={facilityData.has_cctv}
                  onChange={handleFacilityChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value={false}>없음</option>
                  <option value={true}>있음</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 상태 정보 탭 */}
      {activeTab === 'condition' && (
        <div className="space-y-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">건물 상태 정보</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">외벽 균열 상태</label>
                <select
                  name="exterior_crack_status"
                  value={conditionData.exterior_crack_status}
                  onChange={handleConditionChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="양호">양호</option>
                  <option value="보통">보통</option>
                  <option value="불량">불량</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">도장상태</label>
                <select
                  name="paint_status"
                  value={conditionData.paint_status}
                  onChange={handleConditionChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="양호">양호</option>
                  <option value="보통">보통</option>
                  <option value="불량">불량</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">옥상 방수상태</label>
                <select
                  name="roof_waterproof_status"
                  value={conditionData.roof_waterproof_status}
                  onChange={handleConditionChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="양호">양호</option>
                  <option value="보통">보통</option>
                  <option value="불량">불량</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">급수/배수 상태</label>
                <select
                  name="water_supply_status"
                  value={conditionData.water_supply_status}
                  onChange={handleConditionChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="양호">양호</option>
                  <option value="보통">보통</option>
                  <option value="불량">불량</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">보일러 상태</label>
                <select
                  name="boiler_status"
                  value={conditionData.boiler_status}
                  onChange={handleConditionChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="양호">양호</option>
                  <option value="보통">보통</option>
                  <option value="불량">불량</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">소방시설 상태</label>
                <select
                  name="fire_safety_status"
                  value={conditionData.fire_safety_status}
                  onChange={handleConditionChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="양호">양호</option>
                  <option value="보통">보통</option>
                  <option value="불량">불량</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">전기배선 상태</label>
                <select
                  name="electrical_wiring_status"
                  value={conditionData.electrical_wiring_status}
                  onChange={handleConditionChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="양호">양호</option>
                  <option value="보통">보통</option>
                  <option value="불량">불량</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">복도 상태</label>
                <select
                  name="corridor_status"
                  value={conditionData.corridor_status}
                  onChange={handleConditionChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="양호">양호</option>
                  <option value="보통">보통</option>
                  <option value="불량">불량</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">주차장 상태</label>
                <select
                  name="parking_status"
                  value={conditionData.parking_status}
                  onChange={handleConditionChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="양호">양호</option>
                  <option value="보통">보통</option>
                  <option value="불량">불량</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">조명 상태</label>
                <select
                  name="lighting_status"
                  value={conditionData.lighting_status}
                  onChange={handleConditionChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="양호">양호</option>
                  <option value="보통">보통</option>
                  <option value="불량">불량</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">청소상태</label>
                <select
                  name="cleaning_status"
                  value={conditionData.cleaning_status}
                  onChange={handleConditionChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="양호">양호</option>
                  <option value="보통">보통</option>
                  <option value="불량">불량</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 점검 이력 탭 */}
      {activeTab === 'inspection' && (
        <div className="space-y-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">점검 이력</h2>
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">점검 이력 관리 기능은 추후 구현 예정입니다.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RMCBuildingEdit;
